"use client";

import type { SearchEventData } from "@commerce-frontend/gtm-ecommerce";
import { usePathname, useRouter } from "@commerce-frontend/i18n/navigation";
import type { Locale } from "@commerce-frontend/i18n/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { Input } from "@commerce-frontend/ui/base/Input/Input";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useSearchBox } from "react-instantsearch";
import { useMenu } from "~/app/[locale]/(regular-layout)/_components/Header/MenuContext";
import { useNavigation } from "~/lib/navigation-provider";
import {
	deviceData,
	locationDataFromPathname,
	searchData,
	sendGTMEvent,
	userData,
} from "~/lib/send-gtm-event";

export const SearchBar = () => {
	const searchParams = useSearchParams();
	const router = useRouter();
	const locale = useLocale() as Locale;
	const pathname = usePathname();
	const { previousUrl } = useNavigation();
	const t = useTranslations("SearchBar");
	const { searchOpen, setSearchOpen, searchQuery, setSearchQuery } = useMenu();
	const { query, refine } = useSearchBox({
		queryHook,
	});

	// Pressing '/' if not already in a form element will focus the search input
	useKeyDown((event: KeyboardEvent) => {
		if (event.key === "/") {
			if (
				event.target instanceof HTMLInputElement ||
				event.target instanceof HTMLSelectElement ||
				event.target instanceof HTMLTextAreaElement
			) {
				return;
			}
			event.preventDefault();

			document.querySelector<HTMLInputElement>("input[type=search][name=q]")?.focus();
		}
	});

	const handleSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const input = event.currentTarget.elements.namedItem("q") as HTMLInputElement;

		sendGTMEvent<SearchEventData>({
			event: "search",
			timestamp: Date.now(),
			device: deviceData(),
			user: userData(),
			location: locationDataFromPathname({
				locale,
				pathname,
				previousUrl,
			}),
			search: searchData(input.value),
		});

		router.push({ pathname: "/search", query: { q: input.value } }, { locale });

		if (searchParams.get("q") === input.value) {
			setSearchOpen(false);
		}

		document.querySelector<HTMLAnchorElement>('[aria-label="Product listing"] a')?.focus();
	};

	function setQuery(newQuery: string) {
		setSearchQuery(newQuery);

		if (searchOpen) {
			refine(newQuery);
		}
	}

	useEffect(() => {
		if (!searchOpen) {
			setSearchQuery("");
			return;
		}

		setQuery(searchQuery);
	}, [searchOpen]);

	return (
		<form
			action="/search" // if js is disabled, the form will still work
			onSubmit={handleSubmitSearch}
			className="relative flex w-full"
			aria-labelledby="search-button"
			onReset={(e) => {
				e.preventDefault();
				e.stopPropagation();
				setQuery("");
			}}
		>
			<Icon
				icon="search-sm"
				className={cn(
					"absolute top-1/2 left-3 -translate-y-1/2 z-[5] lg:hidden",
					searchOpen && "hidden",
				)}
				aria-hidden={searchOpen}
			/>
			<Button
				onClick={() => setSearchOpen(false)}
				id="search-back-button"
				className={cn(
					"hidden border border-gray-300 border-r-0 shadow-sm rounded-r-none bg-gray-50 lg:hidden hover:shadow-sm",
					searchOpen && "flex max-lg:px-lg hover:bg-gray-50 focus:bg-gray-50 focus:z-[5]",
				)}
				variant="primary"
				type="button"
				title={t("back")}
			>
				<Icon icon="chevron-left" className="size-5 bg-brand-500" />
			</Button>
			<Input
				className={cn(
					"text-md bg-gray-50 lg:focus:bg-white lg:rounded-r-none lg:border-r-0",
					searchOpen
						? "max-lg:rounded-l-none max-lg:border-l-0 max-lg:focus-visible:border-l focus:z-[5]"
						: "max-lg:pl-11",
				)}
				name="q"
				type="search"
				placeholder={searchOpen ? undefined : t("placeholder")}
				autoFocus={searchOpen}
				onClick={(e) => {
					if (!searchOpen) {
						e.preventDefault();
						setSearchOpen(true);
					}
				}}
				onChange={(event) => {
					setQuery(event.currentTarget.value);
				}}
				value={searchQuery}
				autoComplete="off"
			/>
			<Button
				id="search-button"
				className="hidden rounded-l-none lg:flex"
				variant="primary"
				type="submit"
				title={t("search")}
			>
				<Icon icon="search-sm" className="size-5 bg-white" />
				{t("search")}
			</Button>
		</form>
	);
};

// Debounce Algolia search input

let timerId: ReturnType<typeof setTimeout> | undefined = undefined;
const timeout = 300;

function queryHook(query: string, search: (value: string) => void) {
	if (timerId) {
		clearTimeout(timerId);
	}

	timerId = setTimeout(() => search(query), timeout);
}

export const useKeyDown = (handler: (event: KeyboardEvent) => void) =>
	useEffect(() => {
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [handler]);
